import React, { useState, useCallback, useEffect } from "react";
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Button,
  Divider,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../utils/api";
import {
  mergeNotificationPayload,
  normalizeNotificationsResponse,
} from "../../utils/notificationPayload";

/** Stable string id for matching notifications across API shapes */
const getNotificationId = (n) => {
  if (!n) return "";
  const raw = n.id ?? n._id ?? n.notificationId ?? n.uuid;
  return raw != null ? String(raw) : "";
};

const formatTime = (createdAt) => {
  if (createdAt == null) return "";
  try {
    const d =
      typeof createdAt === "number"
        ? createdAt > 1e12
          ? new Date(createdAt)
          : new Date(createdAt * 1000)
        : new Date(createdAt);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const NotificationsMenu = () => {
  const {
    fetchUnreadNotifications,
    fetchUnreadNotificationCount,
    markNotificationRead,
    markAllNotificationsRead,
  } = useApi();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [items, setItems] = useState([]);
  /** Server unread total; null if unread-count failed (badge falls back to items.length) */
  const [unreadTotal, setUnreadTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [handlingId, setHandlingId] = useState(null);
  const [markingAll, setMarkingAll] = useState(false);

  const open = Boolean(anchorEl);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [raw, countBody] = await Promise.all([
        fetchUnreadNotifications(),
        fetchUnreadNotificationCount().catch(() => null),
      ]);
      const list = normalizeNotificationsResponse(raw);
      setItems(
        list.filter((n) => {
          const read =
            n?.isRead === true ||
            n?.read === true ||
            n?.seen === true ||
            n?.readAt != null ||
            n?.seenAt != null;
          return !read;
        })
      );
      if (
        countBody &&
        typeof countBody === "object" &&
        typeof countBody.unreadCount === "number"
      ) {
        setUnreadTotal(countBody.unreadCount);
      } else {
        setUnreadTotal(null);
      }
    } catch (e) {
      setError(e?.message || "Failed to load notifications");
      setItems([]);
      setUnreadTotal(null);
    } finally {
      setLoading(false);
    }
  }, [fetchUnreadNotifications, fetchUnreadNotificationCount]);

  // Prefetch so the badge count is correct before the user opens the menu
  useEffect(() => {
    load();
  }, [load]);

  // Refresh when opening the menu so the list matches the latest server state
  useEffect(() => {
    if (!open) return;
    load();
  }, [open, load]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAll = async () => {
    if (markingAll || items.length === 0) return;
    setMarkingAll(true);
    try {
      await markAllNotificationsRead();
      setItems([]);
      try {
        const body = await fetchUnreadNotificationCount();
        if (body && typeof body.unreadCount === "number") {
          setUnreadTotal(body.unreadCount);
        } else {
          setUnreadTotal(null);
        }
      } catch {
        setUnreadTotal(null);
        await load();
      }
    } catch (e) {
      console.warn("markAllNotificationsRead", e);
      setError(e?.message || "Failed to mark all as read");
    } finally {
      setMarkingAll(false);
    }
  };

  const handleItemClick = async (n) => {
    const id = getNotificationId(n);
    const sk = n.sk;
    const payload = mergeNotificationPayload(n);
    const screen = payload.screen?.toString();

    setHandlingId(id);

    const removeFromList = () => {
      setItems((prev) => prev.filter((x) => getNotificationId(x) !== id));
      setUnreadTotal((u) =>
        u === null
          ? null
          : typeof u === "number" && u > 0
            ? u - 1
            : u
      );
    };

    try {
      const messageConversationId =
        payload.conversationId ?? payload.conversation_id;
      if (screen === "Messages" && messageConversationId) {
        navigate(
          `/messaging?conversationId=${encodeURIComponent(
            String(messageConversationId)
          )}`
        );
      } else if (
        screen === "LogWorkout" &&
        payload.workoutId != null &&
        payload.exerciseIndex != null
      ) {
        const workoutId = String(payload.workoutId);
        const exerciseIndex = String(payload.exerciseIndex);
        const clientUserId =
          payload.userId?.toString() ??
          payload.clientId?.toString() ??
          "";
        const openComments =
          payload.action?.toString() === "openComments" ? "1" : "";
        if (!clientUserId) {
          console.warn("Notification missing client userId for LogWorkout");
          handleClose();
          return;
        }
        const q = new URLSearchParams({
          clientId: clientUserId,
          workoutId,
          exerciseIndex,
        });
        if (openComments) q.set("openComments", "1");
        navigate(`/clients?${q.toString()}`);
      }

      // Drop from local state immediately so the bell count updates right away
      if (id) {
        removeFromList();
      }

      if (id && sk) {
        try {
          await markNotificationRead(id, String(sk));
        } catch (err) {
          console.warn("markNotificationRead after navigate", err);
        }
      }

      handleClose();
    } finally {
      setHandlingId(null);
    }
  };

  const listCount = items.length;
  const badgeCount =
    unreadTotal !== null ? unreadTotal : listCount;

  return (
    <>
      <IconButton
        id="notifications-button"
        aria-label="Notifications"
        onClick={handleOpen}
        sx={{ color: "white" }}
      >
        <Badge
          badgeContent={badgeCount}
          color="error"
          max={99}
          invisible={badgeCount === 0}
        >
          <NotificationsIcon fontSize="large" />
        </Badge>
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: { width: 360, maxWidth: "calc(100vw - 24px)", mt: 1 },
          },
        }}
      >
        <Box sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 600 }}>
            Notifications
          </Typography>
          {listCount > 0 && (
            <Button
              size="small"
              onClick={handleMarkAll}
              disabled={markingAll}
              sx={{ flexShrink: 0 }}
            >
              {markingAll ? "…" : "Mark all read"}
            </Button>
          )}
        </Box>
        <Divider />
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2 }}>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
            <Button size="small" onClick={load} sx={{ mt: 1 }}>
              Retry
            </Button>
          </Box>
        ) : listCount === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography color="text.secondary" variant="body2">
              No unread notifications
            </Typography>
          </Box>
        ) : (
          <List dense sx={{ maxHeight: 360, overflow: "auto", py: 0 }}>
            {items.map((n) => {
              const key = n.id ?? n._id ?? n.notificationId ?? Math.random();
              const title =
                n.title ?? n.subject ?? n.heading ?? "Notification";
              const body =
                n.body ?? n.message ?? n.content ?? n.text ?? "";
              const subtitle = formatTime(
                n.createdAt ?? n.timestamp ?? n.sentAt
              );
              const busy = handlingId === getNotificationId(n);
              return (
                <ListItemButton
                  key={key}
                  alignItems="flex-start"
                  disabled={busy}
                  onClick={() => handleItemClick(n)}
                >
                  <ListItemText
                    primary={title}
                    secondary={
                      <>
                        {body ? (
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.primary"
                            sx={{ display: "block" }}
                          >
                            {body}
                          </Typography>
                        ) : null}
                        {subtitle ? (
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                          >
                            {subtitle}
                          </Typography>
                        ) : null}
                      </>
                    }
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Popover>
    </>
  );
};

export default NotificationsMenu;
